// src/components/Admin/InventoryManagement.js
import React, { useEffect, useState } from "react";
import { db } from "../../api";
import {
  collection,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "../../api";

export default function InventoryManagement() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newItem, setNewItem] = useState({
    name: "",
    category: "",
    size: "",
    quantity: 1,
    dailyRate: 0,
    condition: "excellent",
    description: ""
  });

  // Fetch inventory and categories in real-time
  useEffect(() => {
    const unsubInventory = onSnapshot(collection(db, "inventory"), (snapshot) => {
      const inventoryList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setItems(inventoryList);
    });
    
    const unsubCategories = onSnapshot(collection(db, "equipment_categories"), (snapshot) => {
      const categoryList = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCategories(categoryList);
      if (categoryList.length > 0 && !newItem.category) {
        setNewItem(prev => ({ ...prev, category: categoryList[0].name }));
      }
    });
    
    return () => {
      unsubInventory();
      unsubCategories();
    };
  }, [newItem.category]);

  // Create a new item
  const handleCreateItem = async (e) => {
    e.preventDefault();
    try {
      const selectedCategory = categories.find(cat => cat.name === newItem.category);
      const itemData = {
        name: newItem.name,
        category: newItem.category,
        quantity: Number(newItem.quantity),
        dailyRate: Number(newItem.dailyRate),
        condition: newItem.condition,
        description: newItem.description,
        available: Number(newItem.quantity), // Track available vs total quantity
        createdAt: new Date()
      };
      
      // Only add size if category requires it
      if (selectedCategory && selectedCategory.requiresSize) {
        if (selectedCategory.sizeType === 'number') {
          itemData.size = String(newItem.size);
        } else {
          itemData.size = newItem.size;
        }
      }
      
      await addDoc(collection(db, "inventory"), itemData);
      setNewItem({ 
        name: "", 
        category: categories.length > 0 ? categories[0].name : "", 
        size: "", 
        quantity: 1,
        dailyRate: 0,
        condition: "excellent",
        description: ""
      });
    } catch (err) {
      console.error(err);
    }
  };

  // Update an item (e.g., change quantity)
  const handleUpdateItem = async (itemId, field, value) => {
    try {
      const docRef = doc(db, "inventory", itemId);
      await updateDoc(docRef, { [field]: field === "size" ? String(value) : value });
    } catch (err) {
      console.error(err);
    }
  };

  // Delete an item
  const handleDeleteItem = async (itemId) => {
    try {
      const docRef = doc(db, "inventory", itemId);
      await deleteDoc(docRef);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="flex flex-col space-y-8">
      {/* Inventory List */}
      <div>
        <h4 className="text-burgundy text-lg font-medium mb-4">
          Inventory List
        </h4>
        {items.length === 0 && <p className="text-navy">No items found.</p>}
        {items.map((item) => (
          <div
            key={item.id}
            className="bg-white border border-gray-300 rounded shadow p-4 mb-4"
          >
            <p className="mb-1">
              <strong>Name:</strong> {item.name}
            </p>
            <p className="mb-1">
              <strong>Category:</strong> {item.category}
            </p>
            <p className="mb-1">
              <strong>Condition:</strong> {item.condition}
            </p>
            <p className="mb-1">
              <strong>Daily Rate:</strong> ${item.dailyRate || 0}
            </p>
            {item.description && (
              <p className="mb-1">
                <strong>Description:</strong> {item.description}
              </p>
            )}
            {item.size !== undefined && (
              <div className="mb-1 flex items-center">
                <strong className="mr-2">Size:</strong>
                <input
                  type={typeof item.size === 'number' ? "number" : "text"}
                  value={item.size}
                  onChange={(e) => {
                    const value = typeof item.size === 'number' ? Number(e.target.value) : e.target.value;
                    handleUpdateItem(item.id, "size", value);
                  }}
                  className="border border-gray-300 rounded p-1 w-20 focus:outline-none focus:border-burgundy"
                />
              </div>
            )}
            <div className="mb-1 flex items-center">
              <strong className="mr-2">Total Quantity:</strong>
              <input
                type="number"
                value={item.quantity}
                onChange={(e) =>
                  handleUpdateItem(item.id, "quantity", Number(e.target.value))
                }
                className="border border-gray-300 rounded p-1 w-20 focus:outline-none focus:border-burgundy"
              />
            </div>
            <div className="mb-2 flex items-center">
              <strong className="mr-2">Available:</strong>
              <span className="text-navy">{item.available ?? item.quantity}</span>
            </div>
            <button
              onClick={() => handleDeleteItem(item.id)}
              className="bg-burgundy text-white py-1 px-3 rounded hover:bg-opacity-90 transition"
            >
              Delete
            </button>
          </div>
        ))}
      </div>

      {/* Add New Item */}
      <div>
        <h4 className="text-burgundy text-lg font-medium mb-4">
          Add New Inventory Item
        </h4>
        <form
          onSubmit={handleCreateItem}
          className="bg-white border border-gray-300 rounded shadow p-4 space-y-4"
        >
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">Name:</label>
            <input
              type="text"
              value={newItem.name}
              onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
              required
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">Category:</label>
            <select
              value={newItem.category}
              onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
              required
            >
              {categories.length === 0 ? (
                <option value="">No categories available - create one first</option>
              ) : (
                categories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))
              )}
            </select>
          </div>
          {categories.find(cat => cat.name === newItem.category)?.requiresSize && (
            <div className="flex flex-col">
              <label className="text-sm font-medium text-navy">Size:</label>
              <input
                type={categories.find(cat => cat.name === newItem.category)?.sizeType === 'number' ? "number" : "text"}
                value={newItem.size}
                onChange={(e) => setNewItem({ ...newItem, size: e.target.value })}
                placeholder={categories.find(cat => cat.name === newItem.category)?.sizeType === 'number' ? "e.g., 180" : "e.g., Large"}
                required
                className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
              />
            </div>
          )}
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">Quantity:</label>
            <input
              type="number"
              value={newItem.quantity}
              onChange={(e) =>
                setNewItem({ ...newItem, quantity: e.target.value })
              }
              min="1"
              required
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">Daily Rate ($):</label>
            <input
              type="number"
              step="0.01"
              value={newItem.dailyRate}
              onChange={(e) =>
                setNewItem({ ...newItem, dailyRate: e.target.value })
              }
              min="0"
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">Condition:</label>
            <select
              value={newItem.condition}
              onChange={(e) => setNewItem({ ...newItem, condition: e.target.value })}
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            >
              <option value="excellent">Excellent</option>
              <option value="good">Good</option>
              <option value="fair">Fair</option>
              <option value="needs_repair">Needs Repair</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium text-navy">Description (Optional):</label>
            <textarea
              value={newItem.description}
              onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
              placeholder="Additional details about this item"
              rows="2"
              className="border border-gray-300 rounded p-2 focus:outline-none focus:border-burgundy"
            />
          </div>
          <button
            type="submit"
            className="bg-navy text-white py-2 px-4 rounded hover:bg-opacity-90 transition"
          >
            Add Item
          </button>
        </form>
      </div>
    </div>
  );
}
